/*!
 * liveExamples.js — Oscilla Live's examples, one at random, started at once
 * © 2026 Rob Canning — GPLv3
 *
 * Short pieces in public/live-examples/ (index.json lists them), each a
 * few lines that sound and draw on their own -- no files, so they play on
 * any host. A random one (never the one just playing) opens as a session
 * of its own and runs, as Hydra's shuffle does: the click that asks for it
 * is the one a browser wants before it lets sound start.
 */

import { handleCueTrigger } from "../cues/cueDispatcher.js";
import { parseCueToAST } from "../parser/parser.js";
import { handleImageCue } from "../cues/image.js";
import { handleVideoCueFromAST } from "../cues/video.js";
import { clearStageGrid } from "../cues/grid.js";
import { mirrorClear } from "./oscillaLive.js";
import { clearScatter } from "../cues/scatter.js";
import { runReset } from "../system/teardown.js";

import { exampleHeader } from "./exampleHeader.js";

const BASE = "live-examples/";
let last = null;

const examplesIndex = async () => (await fetch(`${BASE}index.json`, { cache: "no-store" })).json();

/** One example's code, as it is on the server now (null when it cannot be fetched). */
export async function exampleCode(file) {
  try {
    const res = await fetch(`${BASE}${file}`, { cache: "no-store" });
    if (!res.ok) return null;
    last = file;
    return await res.text();
  } catch {
    return null;
  }
}

/** A random example's code (not the last one's), or null when there are none to fetch. */
export async function randomExample() {
  try {
    const files = await examplesIndex();
    const choice = files.length > 1 ? files.filter(f => f !== last) : files;
    const file = choice[Math.floor(Math.random() * choice.length)];
    return file ? exampleCode(file) : null;
  } catch {
    return null;
  }
}

/** Every example, in the index's order: { file, title, about } (its first line). */
export async function listExamples() {
  try {
    const files = await examplesIndex();
    return await Promise.all(files.map(async file => {
      const code = await (await fetch(`${BASE}${file}`, { cache: "no-store" })).text().catch(() => "");
      return { file, ...exampleHeader(code, file) };
    }));
  } catch {
    return [];
  }
}

/**
 * The stage cleared for the next piece: sound, animations, texts, the
 * screen, shapes, pictures, film, the tempo. Done when it resolves.
 * hush() and still(all) are the "sound" and "stage" resets (teardown.js:
 * what each loaded module registered, the viz() and target > lines still
 * waiting too); what still(all) leaves on the stage, the things drawn,
 * shown and filmed, a new piece takes off here -- each straight to its
 * handler, not as a line through the dispatcher, which loads image() and
 * video() on first use: the clearing would else land after the piece had
 * started.
 */
export async function clearStage({ keepTempo = false, mirror = true } = {}) {
  // the projector's stage too, before the next piece's lines reach it (in a
  // jam every laptop's) -- unless the clear came from another laptop
  if (mirror) mirrorClear();
  const run = line => { try { handleCueTrigger(line, false, true, null, { live: true }); } catch { /* the next */ } };
  runReset("sound", "stage");
  run("adc(off:1)");          // a new piece: the input released, its boxes gone
  (await import("../cues/cam.js")).stopAllCams();   // ... and the camera
  run("shape(remove:all)");
  await handleImageCue(parseCueToAST("image(remove:all)"), null);
  await handleVideoCueFromAST(parseCueToAST("video(remove:all)"));
  if (!keepTempo) run("tempo(60)");             // a score starting again (console(clear)) keeps its own
  clearStageGrid();
  clearScatter(document.querySelector("#scoreContainer svg"));   // a scatter of the whole stage, back
}
